// ---------------------------------------------------------------------------
// useWizardOptions — fetches wizard configuration from the backend once,
// caches it for the lifetime of the app. All wizard step components consume
// this hook instead of hardcoding option arrays.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { fetchWizardOptions, type WizardOptions } from '../lib/api';

let cachedOptions: WizardOptions | null = null;
let fetchPromise: Promise<WizardOptions> | null = null;

export function useWizardOptions(): {
  options: WizardOptions | null;
  loading: boolean;
  error: string | null;
} {
  const [options, setOptions] = useState<WizardOptions | null>(cachedOptions);
  const [loading, setLoading] = useState(!cachedOptions);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedOptions) {
      setOptions(cachedOptions);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetchWizardOptions();
    }

    fetchPromise
      .then((data) => {
        cachedOptions = data;
        setOptions(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[useWizardOptions] Failed to load options:', err);
        setError(err.message ?? 'Failed to load wizard configuration');
        setLoading(false);
      });
  }, []);

  return { options, loading, error };
}
